import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Globe, 
  ArrowLeft, 
  CheckCircle2, 
  Loader2,
  ArrowRight,
  Clock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DomainClaim = () => {
  const navigate = useNavigate();
  const { purchaseId } = useParams();
  const { user } = useContext(AuthContext);
  const [domainData, setDomainData] = useState(null);
  const [namecheapUsername, setNamecheapUsername] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    fetchDomainDetails();
  }, [user, purchaseId]);

  const fetchDomainDetails = async () => {
    try {
      const response = await axios.get(`${API}/domains/${purchaseId}`);
      setDomainData(response.data);
    } catch (error) {
      console.error("Error fetching domain:", error);
      toast.error("Failed to load domain details");
      navigate("/dashboard");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitClaim = async (e) => {
    e.preventDefault();
    
    if (!namecheapUsername.trim()) {
      toast.error("Please enter your Namecheap username");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/domains/claim`, {
        purchase_id: purchaseId,
        namecheap_username: namecheapUsername.trim()
      });
      
      toast.success("Ownership request sent!");
      setDomainData(prev => ({
        ...prev,
        status: "awaiting_acceptance",
        namecheap_username: namecheapUsername.trim()
      }));
    } catch (error) {
      console.error("Claim error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyAcceptance = async () => {
    setIsVerifying(true);
    try {
      await axios.post(`${API}/domains/verify-acceptance`, {
        purchase_id: purchaseId
      });
      
      toast.success("Verified!");
      setDomainData(prev => ({
        ...prev,
        status: "ready_to_connect"
      }));
    } catch (error) {
      console.error("Verify error:", error);
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSkipForNow = () => {
    toast.info("You can claim your domain anytime from your dashboard");
    navigate("/dashboard");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const status = domainData?.status || "ready_to_claim";

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Navigation */}
      <nav className="nav-glass sticky top-0 z-50 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
            <span className="text-xl font-semibold text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Bloomwright Studio
            </span>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-[#78716C] hover:text-[#2C332E]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <main className="max-w-lg mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-[#C77D63]/10 flex items-center justify-center mx-auto mb-4">
            <Globe className="w-7 h-7 text-[#C77D63]" />
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold text-[#2C332E] mb-2">
            Claim your domain
          </h1>
          <p className="text-[#78716C]">
            A small handoff. Then it's yours.
          </p>
        </div>

        {/* Domain Display */}
        <div className="bg-[#C77D63]/10 rounded-xl p-4 text-center mb-8">
          <p className="text-sm text-[#78716C] mb-1">Your domain</p>
          <p className="text-xl font-semibold text-[#C77D63]">{domainData?.domain}</p>
        </div>

        {/* Ready to Claim State */}
        {status === "ready_to_claim" && (
          <>
            {/* What you'll do */}
            <div className="mb-6">
              <p className="text-[#78716C] text-sm leading-relaxed">
                Enter your Namecheap username so we can send the domain to your account.
                You'll receive a Namecheap email to accept ownership.
              </p>
            </div>

            {/* Form */}
            <Card className="bg-white rounded-xl border-[#E6E1DB]/50 shadow-soft mb-6">
              <CardContent className="p-6">
                <form onSubmit={handleSubmitClaim}>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="namecheap-username" className="text-[#2C332E] font-medium">
                        Namecheap username
                      </Label>
                      <Input
                        id="namecheap-username"
                        type="text"
                        placeholder="yourusername (not your email)"
                        value={namecheapUsername}
                        onChange={(e) => setNamecheapUsername(e.target.value)}
                        className="mt-2 rounded-lg border-[#E6E1DB] focus:border-[#8DA399] focus:ring-[#8DA399]"
                        data-testid="namecheap-username-input"
                      />
                      <p className="text-xs text-[#78716C] mt-2">
                        You can find this in the top-right corner of your Namecheap account.
                      </p>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full py-5"
                      data-testid="submit-claim-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Send ownership request"
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <div className="text-center">
              <button
                onClick={handleSkipForNow}
                className="text-sm text-[#78716C] hover:text-[#2C332E] underline"
              >
                I'll do this later
              </button>
            </div>

            {/* Footnote */}
            <p className="text-xs text-[#78716C] text-center mt-6">
              We never ask for your password.
            </p>
          </>
        )}

        {/* Awaiting Acceptance State */}
        {status === "awaiting_acceptance" && (
          <>
            <Card className="bg-[#8DA399]/10 rounded-xl border border-[#8DA399]/30 mb-6">
              <CardContent className="p-6 text-center">
                <CheckCircle2 className="w-10 h-10 text-[#8DA399] mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-[#2C332E] mb-2">
                  Request sent
                </h2>
                <p className="text-sm text-[#78716C]">
                  Check your email for a Namecheap ownership request.<br />
                  Once you accept it, you can connect your domain to your site.
                </p>
              </CardContent>
            </Card>

            {/* Status */}
            <Card className="bg-white rounded-xl border-[#E6E1DB]/50 shadow-soft mb-6">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <span className="font-medium text-[#2C332E] text-sm">Awaiting acceptance</span>
                </div>
                <p className="text-sm text-[#78716C] mb-4">
                  Your request has been sent. Accept it in Namecheap, then return here.
                </p>
                <Button
                  onClick={handleVerifyAcceptance}
                  disabled={isVerifying}
                  className="w-full bg-[#8DA399] hover:bg-[#7A8F86] text-white rounded-full"
                  data-testid="verify-acceptance-btn"
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    "I've accepted — check status"
                  )}
                </Button>
              </CardContent>
            </Card>
          </>
        )}

        {/* Ready to Connect State */}
        {status === "ready_to_connect" && (
          <Card className="bg-[#8DA399]/10 rounded-xl border border-[#8DA399]/30">
            <CardContent className="p-6 text-center">
              <CheckCircle2 className="w-10 h-10 text-[#8DA399] mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-[#2C332E] mb-2">
                Ownership verified
              </h2>
              <p className="text-sm text-[#78716C] mb-4">
                Your domain is ready to connect.
              </p>
              <Button
                onClick={() => navigate(`/domain-connect/${purchaseId}`)}
                className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-6"
                data-testid="continue-connect-btn"
              >
                Continue to Connect Domain
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Live State */}
        {status === "live" && (
          <Card className="bg-green-50 rounded-xl border border-green-200">
            <CardContent className="p-6 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-[#2C332E] mb-2">
                Domain is live
              </h2>
              <p className="text-sm text-[#78716C] mb-4">
                {domainData?.domain} is connected.
              </p>
              <Button
                onClick={() => navigate("/dashboard")}
                variant="outline"
                className="rounded-full"
              >
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default DomainClaim;
